import { useState } from "react";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useMainContext } from "context/useMainContext";
import ModalAddPartner from "./ModalAddPartner";
import toast from "react-hot-toast";
import ModalContactPersonAssign from "./ModalContactPersonAssign";
import { supabase } from "supabase";
import parseProfileUrl from "./utils/parseProfileUrl";
import parseCsvProfiles from "./utils/parseCsvProfiles";
import { getFieldByKey } from "../../../hooks/useTableColumnsConfig";
import processDeduplication from "./utils/processDeduplication";
import insertExtraFields from "./utils/insertExtraFields";
import createStatusHistory from "./utils/createStatusHistory";

export default function AddPartner() {
  const { isGuest, setRows, user, setLoadingData } = useMainContext();
  const [profileUrl, setProfileUrl] = useState("");
  const [justAddedProfile, setJustAddedProfile] = useState(null);
  const [skippedProfiles, setSkippedProfiles] = useState([]);
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const handleOpenAddProfile = () => {
    setShowAddPartnerModal(true);
    setProfileUrl("");
    setSkippedProfiles([]);
  };

  const handleAddPartner = async (params) => {
    try {
      setLoadingData(true);
      
      // Parsing
      const isCsvUpload = params.target?.files?.[0];
      let normalizedProfiles = [];

      if (isCsvUpload) {
        const { csvProfiles, parseErrors } = await parseCsvProfiles({ file: params.target.files[0] });
        if (parseErrors.length) toast.error(`${parseErrors.length} row(s) skipped.`);
        normalizedProfiles = csvProfiles;
        params.target.value = "";
      } else {
        const { platform, username, link } = parseProfileUrl({ url: params });
        normalizedProfiles = [{ username: username?.toLowerCase(), platform: platform?.toLowerCase(), link }];
      }

      if (!normalizedProfiles.length) return toast.error("No profiles to add.");

      // Fetch existing
      const { data: existingProfiles, error: existingError } = await supabase
        .from("influencers")
        .select("id,username,platform,link,created_at")
        .in("username", normalizedProfiles.map((p) => p.username));

      if (existingError) throw new Error(`Failed to fetch existing profiles: ${existingError.message}`);

      // Deduplication
      const { profilesToInsert, skippedProfiles, additionalDataMap, getProfileKey } = processDeduplication({
        normalizedProfiles,
        existingProfiles,
        getFieldByKey
      });

      // Show error if single profile import is a duplicate
      if (!isCsvUpload && skippedProfiles.length > 0) {
        toast.error("Import failed: This influencer already exists in the system. No changes were applied.");
        setLoadingData(false);
        return;
      }

      // Show error if all CSV profiles are duplicates
      if (!profilesToInsert.length) {
        if (skippedProfiles.length > 0) {
          toast.error(`Import failed: All ${skippedProfiles.length} profile(s) already exist in the system. No changes were applied.`);
          setSkippedProfiles(skippedProfiles);
        } else {
          toast.error("No new profiles to add.");
        }
        setLoadingData(false);
        return;
      }

      // Insert
      const { data: insertedProfiles, error: insertError } = await supabase
        .from("influencers")
        .insert(profilesToInsert)
        .select();

      if (insertError) throw new Error(`Failed to insert profiles: ${insertError.message}`);

      // Extra fields
      if (isCsvUpload) {
        await insertExtraFields({ insertedProfiles, additionalDataMap, getProfileKey, getFieldByKey });
      }

      // Status history
      await createStatusHistory({ insertedProfiles, userName: user?.name });

      // Success message
      const hasSkipped = skippedProfiles.length > 0;
      const successMessage = hasSkipped
        ? `Added ${profilesToInsert.length} profile(s). ${skippedProfiles.length} profile(s) skipped (already exist in the system).`
        : `Added ${profilesToInsert.length} profile(s).`;
      toast.success(successMessage, { duration: 10000 });

      // Update UI
      setRows((prev) => [...insertedProfiles, ...prev]);
      if (hasSkipped) setSkippedProfiles(skippedProfiles);
      if (!isCsvUpload) {
        setJustAddedProfile(insertedProfiles?.[0]);
        setProfileUrl("");
        setShowAddPartnerModal(hasSkipped);
        setShowContactModal(true);
      }
    } catch (error) {
      toast.error(`Failed to add profile(s): ${error.message}`);
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={handleOpenAddProfile} disabled={isGuest}>
        <AddIcon fontSize="small" sx={{ mr: 1 }} />
        Add partners
      </Button>

      <ModalAddPartner
        {...{
          open: showAddPartnerModal,
          setOpen: setShowAddPartnerModal,
          handleAddPartner,
          profileUrl,
          setProfileUrl,
          skippedProfiles,
        }}
      />

      <ModalContactPersonAssign
        {...{
          open: showContactModal,
          setOpen: setShowContactModal,
          justAddedProfile,
        }}
      />
    </>
  );
}
